import { useEffect, useMemo, useState } from "react";
import { CalendarClock, MapPin, RotateCw, Search, UserRound, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../services/apiService";
import { ageGroupLabels, apiError, dateKey, day, duration, monthFromKey, time } from "./schedule/scheduleUtils";
import { MonthCalendar, startOfMonth } from "./schedule/MonthCalendar";
import { formatMoney } from "./payments/paymentUtils";
import { Container, SiteFooter, SiteHeader } from "./SiteChrome";

export function SchedulePage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [age, setAge] = useState("all");
  const [month, setMonth] = useState(() => startOfMonth());
  const [selectedKey, setSelectedKey] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/schedule/public/sessions", { from: new Date().toISOString() });
      setSessions(response.data.sessions || []);
    } catch (requestError) {
      setError(apiError(requestError, "Unable to load upcoming sessions"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(() => sessions.filter((session) => (
    (age === "all" || session.ageGroup === age || session.ageGroup === "all")
    && [session.title, session.location, session.program?.name, session.coach?.firstName, session.coach?.lastName]
      .filter(Boolean).join(" ").toLowerCase().includes(search.toLowerCase())
  )), [sessions, age, search]);

  const groupedByDate = useMemo(() => {
    const map = new Map();
    visible.forEach((session) => {
      const key = dateKey(session.startsAt);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(session);
    });
    return map;
  }, [visible]);
  const availableKeys = useMemo(() => [...groupedByDate.keys()].sort(), [groupedByDate]);

  useEffect(() => {
    if (selectedKey && groupedByDate.has(selectedKey)) return;
    const nextKey = availableKeys[0] || "";
    setSelectedKey(nextKey);
    if (nextKey) setMonth(monthFromKey(nextKey));
  }, [availableKeys, groupedByDate, selectedKey]);

  const daySessions = groupedByDate.get(selectedKey) || [];
  const openSpots = sessions.reduce((sum, item) => sum + Math.max(0, item.capacity - (item.occupancy?.booked || 0)), 0);

  return <>
    <SiteHeader />
    <main className="public-schedule">
      <section className="public-schedule-hero">
        <Container>
          <p className="eyebrow">Training calendar</p>
          <h1>Find your next<br /><em>training session.</em></h1>
          <p>Browse live availability, find the right age group, and reserve through your parent account.</p>
          <div className="public-schedule-hero-actions">
            <Link className="button button--lime" to="/register">Create parent account</Link>
            <Link className="button button--ghost" to="/login">Sign in to book</Link>
          </div>
          <div className="public-schedule-summary">
            <span><strong>{sessions.length}</strong> upcoming sessions</span>
            <span><strong>{openSpots}</strong> open spots</span>
            <span><strong>Live</strong> availability</span>
          </div>
        </Container>
      </section>
      <section className="public-schedule-list">
        <Container>
          <div className="public-schedule-toolbar">
            <div><p className="eyebrow">Upcoming</p><h2>Choose your session</h2></div>
            <label className="public-schedule-search">
              <Search size={17} /><span className="sr-only">Search schedule</span>
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search program, coach, location…" />
            </label>
          </div>
          <div className="program-filters" aria-label="Filter by age group">
            {Object.entries(ageGroupLabels).map(([value, label]) => <button type="button" key={value} aria-pressed={age === value} className={`program-filter-chip ${age === value ? "is-active" : ""}`} onClick={() => setAge(value)}>{label}</button>)}
          </div>
          {error && <div className="public-schedule-error" role="alert"><span>{error}</span><button type="button" onClick={load}><RotateCw size={14} /> Retry</button></div>}
          {loading ? <div className="public-schedule-loading" role="status"><i /> Loading training calendar…</div>
            : !error && visible.length === 0 ? <div className="public-schedule-empty"><CalendarClock /><h3>No sessions match</h3><p>Adjust your filters or check back as new training times are released.</p></div>
              : <>
                <MonthCalendar sessions={visible} month={month} onMonthChange={setMonth} selectedKey={selectedKey} onSelectDate={setSelectedKey} emptyLabel="No sessions scheduled this month." />
                {daySessions.length > 0 && <div className="public-schedule-day">
                  <header>
                    <h3>{day(daySessions[0].startsAt)}</h3>
                    <em>{daySessions.length} {daySessions.length === 1 ? "session" : "sessions"}</em>
                  </header>
                  <div>{daySessions.map((session) => {
                    const booked = session.occupancy?.booked || 0;
                    const remaining = Math.max(0, session.capacity - booked);
                    return <article key={session._id} className="public-session-card">
                      <div className="public-session-time"><strong>{time(session.startsAt)}</strong><span>{time(session.endsAt)}</span><small>{duration(session.startsAt, session.endsAt)}</small></div>
                      <div className="public-session-body">
                        <div><p>{session.program?.name || "Formula training"}</p><h4>{session.title}</h4></div>
                        <div className="public-session-meta"><span><MapPin size={14} />{session.location}</span><span><UserRound size={14} />{session.coach?.firstName} {session.coach?.lastName}</span><span><Users size={14} />{ageGroupLabels[session.ageGroup]}</span></div>
                        <div className="public-session-capacity"><div><span>{booked}/{session.capacity} booked</span><span className={remaining ? "is-open" : "is-wait"}>{remaining ? `${remaining} spots left` : "Waitlist open"}</span></div><i><b style={{ width: `${Math.min(100, booked / session.capacity * 100)}%` }} /></i></div>
                      </div>
                      {remaining && session.dropInPriceCents > 0
                        ? <Link to={`/book-session/${session._id}`} className="public-session-cta">Book — {formatMoney(session.dropInPriceCents)}</Link>
                        : <Link to="/login" className="public-session-cta">{remaining ? "Sign in to book" : "Join waitlist"}</Link>}
                    </article>;
                  })}</div>
                </div>}
              </>}
        </Container>
      </section>
    </main>
    <SiteFooter />
  </>;
}
